/**
 * Admin coupon management — CRUD for discount coupons.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Plus, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { Input } from '../ui/atoms/Input';
import { Label } from '../ui/label';
import { Button } from '@/components/ui/atoms/Button';
import { DataTableOrganism } from '@/components/ui/organisms';
import {
  AccentBarHeading,
  IllustratedEmptyState,
} from '@/components/ui/molecules';
import { SummarizedListSectionTemplate } from '@/components/ui/templates';
import {
  createCouponTableColumns,
  mapCouponToRow,
  type Coupon,
  type CouponTableRow,
} from './couponTable.columns';

const apiUrl = import.meta.env.VITE_API_SERVER_URL;

interface CouponForm {
  code: string;
  discount_percent: string;
  max_uses: string;
  expires_at: string;
  linked_email: string;
}

const EMPTY_FORM: CouponForm = {
  code: '',
  discount_percent: '',
  max_uses: '',
  expires_at: '',
  linked_email: '',
};

export const CouponManagement = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<CouponForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchCoupons = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${apiUrl}/api/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCoupons(await res.json());
    } catch {
      toast.error(i18n.t('common:toasts.admin.coupons_load_failed'));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = await getAccessTokenSilently();

    const body: Record<string, unknown> = {
      code: form.code,
      discount_percent: parseFloat(form.discount_percent),
    };
    if (form.max_uses) body.max_uses = parseInt(form.max_uses);
    if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
    if (form.linked_email) body.linked_email = form.linked_email;

    const url = editingId
      ? `${apiUrl}/api/admin/coupons/${editingId}`
      : `${apiUrl}/api/admin/coupons`;

    const res = await fetch(url, {
      method: editingId ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (res.ok) {
      toast.success(editingId ? i18n.t('common:toasts.admin.coupon_updated') : i18n.t('common:toasts.admin.coupon_created'));
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchCoupons();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || i18n.t('common:toasts.admin.coupon_save_failed'));
    }
  };

  const handleDelete = useCallback(async (id: string) => {
    const token = await getAccessTokenSilently();
    const res = await fetch(`${apiUrl}/api/admin/coupons/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      toast.success(i18n.t('common:toasts.admin.coupon_deactivated'));
      fetchCoupons();
    }
  }, [getAccessTokenSilently, fetchCoupons]);

  const startEdit = useCallback((c: Coupon) => {
    setForm({
      code: c.code,
      discount_percent: String(c.discount_percent),
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : '',
      linked_email: c.linked_email || '',
    });
    setEditingId(c.id);
    setShowForm(true);
  }, []);

  const tableRows = useMemo(() => coupons.map(mapCouponToRow), [coupons]);
  const columns = useMemo(
    () => createCouponTableColumns({ onEdit: startEdit, onDeactivate: handleDelete }),
    [startEdit, handleDelete]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<Ticket />}
          title="No coupons created yet"
          description="Create a coupon code to see it listed here."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<Ticket />}
        title="No matching coupons"
        description="Try another code, email, or status."
      />
    );
  }, [tableRows.length]);

  if (loading) {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading coupons…"
      />
    );
  }

  return (
    <div className="flex-1 min-h-0 h-full flex flex-col gap-6">
      {showForm && (
        <form onSubmit={handleSubmit} className="shrink-0 bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Code *</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="GITEX20"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Discount % *</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                placeholder="20"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Max Uses (empty = unlimited)</Label>
              <Input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                placeholder="100"
              />
            </div>
            <div>
              <Label className="text-xs">Expires At</Label>
              <Input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Linked Email (empty = anyone)</Label>
              <Input
                type="email"
                value={form.linked_email}
                onChange={(e) => setForm({ ...form, linked_email: e.target.value })}
                placeholder="specific@user.com"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              label={editingId ? 'Update' : 'Create'}
              intent="secondary"
              size="sm"
              htmlType="submit"
            />
            <Button
              label="Cancel"
              type="outlined"
              intent="muted"
              size="sm"
              onClick={() => { setShowForm(false); setEditingId(null); }}
            />
          </div>
        </form>
      )}

      <div className="flex-1 min-h-0">
        <DataTableOrganism<CouponTableRow>
          columns={columns}
          data={tableRows}
          pageSize={10}
          fillHeight
          enableSelection
          enableSearch
          enableColumnVisibility
          columnVisibilityLabel="Column visibility"
          toolbarTitle={<AccentBarHeading>Coupon Codes</AccentBarHeading>}
          searchPlaceholder="Search by code, email, status…"
          searchKeys={['code', 'linkedEmail', 'statusDisplay']}
          paginationItemLabel="coupons"
          toolbarEndSlot={
            <Button
              type="contained"
              intent="secondary"
              size="md"
              label="New Coupon"
              icon={<Plus />}
              onClick={() => {
                setForm(EMPTY_FORM);
                setEditingId(null);
                setShowForm(!showForm);
              }}
            />
          }
          dataTableProps={{ emptyState }}
        />
      </div>
    </div>
  );
};

export default CouponManagement;
